export interface DomainEvent<T = unknown> {
  type: string;
  payload: T;
  timestamp: number;
  userId?: string;
}

export type EventHandler<T = unknown> = (event: T) => void | Promise<void>;

/**
 * Event Bus for managing application-wide events
 *
 * Note: Handlers are stored with `unknown` type for flexibility.
 * Type safety is maintained at subscription time through generic parameter T.
 */
export class EventBus {
  private handlers: Map<string, EventHandler<unknown>[]> = new Map();

  /**
   * Subscribe to an event type
   * @param eventType - Type of event to subscribe to
   * @param handler - Handler function that will be called when event is published
   */
  subscribe<T = unknown>(eventType: string, handler: EventHandler<T>): void {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, []);
    }
    // Store handler - type assertion needed due to Map storing handlers of different types
    this.handlers.get(eventType)!.push(handler as EventHandler<unknown>);
  }

  unsubscribe<T = unknown>(eventType: string, handler: EventHandler<T>): void {
    const handlers = this.handlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler as EventHandler<unknown>);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  async publish(event: DomainEvent<unknown>): Promise<void> {
    const handlers = this.handlers.get(event.type) || [];
    await Promise.all(handlers.map((handler) => handler(event)));
  }

  clearAllHandlers(): void {
    this.handlers.clear();
  }
}

// События для отвязки записей
export const RECORD_UNLINKED_EVENT = "record.unlinked";
export const RECORD_UNLINK_FAILED_EVENT = "record.unlink.failed";

// События для привязки записей к проектам
export const RECORD_LINKED_EVENT = "record.linked";
export const RECORD_LINK_FAILED_EVENT = "record.link.failed";
